import { useState } from "react";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import { Auth, Amplify } from "aws-amplify";
import { userInfoState, accessTokenState } from "../global-state";
import { useBackdrop } from "../common/backdrop";
import { USER_AUTH_CONFIG } from "../aws-config";

const useUserAuth = () => {
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [accessToken, setAccessToken] = useRecoilState(accessTokenState);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { openBackdrop, closeBackdrop } = useBackdrop();
  const history = useHistory();

  const fetchUserInfo = async idToken => {
    const response = await fetch(
      `${USER_AUTH_CONFIG.SAND_CAT}/auth/user/info`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      },
    );
    const jsonData = await response.json();
    if (!response.ok) {
      throw new Error(jsonData.error || "Failed to fetch user info");
    }
    return jsonData;
  };

  const checkUserAuthState = async () => {
    try {
      Amplify.configure(USER_AUTH_CONFIG);
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      setAccessToken(accessToken);
      const userInfo = await fetchUserInfo(idToken);
      setUserInfo(userInfo);
      setIsAuthenticated(true);
      closeBackdrop();
      return { isAuthenticated: true, userInfo, idToken, accessToken };
    } catch (error) {
      handleAuthError(error);
      return { isAuthenticated: false, userInfo: null, idToken: null, accessToken: null };
    }
  };

  const refreshUserToken = async () => {
    try {
      Amplify.configure(USER_AUTH_CONFIG);
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();
      setAccessToken(accessToken);
      return accessToken;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  };

  const handleAuthError = async error => {
    console.error("Authentication error:", error);

    if (
      typeof error === "string" &&
      error.toLowerCase() === "no current user"
    ) {
      redirectToLogin();
      return;
    }

    if (typeof error === "object" && error !== null) {
      const { name, code, message } = error;

      if (
        name === "NotAuthorizedException" ||
        code === "NotAuthorizedException" ||
        name === "TokenExpiredError" ||
        (message && message.includes("expired")) ||
        (message && message.includes("Invalid token"))
      ) {
        console.warn("Token expired or invalid, redirecting to login...");
        redirectToLogin();
        return;
      }

      if (message && message.includes("Failed to fetch")) {
        openBackdrop("Cannot reach the server. Please try again later.");
        return;
      }
    }

    openBackdrop("An unexpected error occurred. Please try again.");
  };

  const redirectToLogin = () => {
    setIsAuthenticated(false);
    history.push("/user-login");
  };

  const signOut = async () => {
    try {
      Amplify.configure(USER_AUTH_CONFIG);
      await Auth.signOut();
      setIsAuthenticated(false);
      setUserInfo(null);
      setAccessToken(null);
      history.push("/user-login");
    } catch (error) {
      console.error("Error during sign-out:", error);
      openBackdrop("Failed to sign out. Please try again.");
    }
  };

  return {
    isAuthenticated,
    userInfo,
    checkUserAuthState,
    refreshUserToken,
    signOut,
  };
};

export default useUserAuth;
